import { Input } from '../../common/input.component';
import { Link } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { AuthService } from '../../../services/auth.service';
import { ProfileService } from '../../../services/profile.service';
import { phoneValidation, required } from '../../../utils/validations';
import React, { useRef, useState } from 'react';
import { Captcha } from '../../captcha/Captcha';
import {
  captchaResultToBody,
  CaptchaHandle,
  CaptchaResult,
} from '../../captcha/captcha-types';

type Fields = {
  phone: string;
  password: string;
};

type Props = {
  userIsClient: boolean;
};

export const LoginScreen = ({ userIsClient }: Props) => {
  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<Fields>();

  const captchaRef = useRef<CaptchaHandle>(null);
  const [captchaResult, setCaptchaResult] = useState<CaptchaResult | null>(
    null,
  );
  const [captchaPending, setCaptchaPending] = useState(false);

  const onSubmit =
    (captcha: CaptchaResult) =>
    ({ phone, password }: Fields) => {
      AuthService.login({
        username: phone,
        password,
        source: 'admin',
        ...captchaResultToBody(captcha),
      })
        .then(() => ProfileService.getCurrentUser())
        .catch(() => {
          // Неуспешный логин сжигает одноразовый токен капчи: сбрасываем и
          // перемонтируем виджет, иначе следующий сабмит уйдёт со «сгоревшим»
          // токеном и зациклится на 400 (как уже сделано на клиенте).
          captchaRef.current?.reset();
          setCaptchaResult(null);
        });
    };

  return (
    <div className="full-height d-flex flex-column justify-content-between">
      <div className="flex-grow-1 d-flex justify-content-center align-items-center">
        <div className="auth-wrapper">
          <h3 className="mb-4">Войти в личный кабинет</h3>
          <form
            className="needs-validation"
            onSubmit={(e) => {
              e.preventDefault();
              if (!captchaResult) {
                // Enter/сабмит без пройденной капчи: запускаем виджет (как на
                // клиенте), а не молча выходим. captchaPending — от повторов.
                if (!captchaPending) {
                  setCaptchaPending(true);
                  captchaRef.current?.execute();
                }
                return;
              }
              handleSubmit(onSubmit(captchaResult))(e);
            }}
          >
            <Captcha
              ref={captchaRef}
              onSuccess={(result) => {
                setCaptchaPending(false);
                setCaptchaResult(result);
                handleSubmit(onSubmit(result))();
              }}
              onError={(kind) => {
                setCaptchaPending(false);
                if (kind === 'closed') return;
                // network/javascript failures: mirror the previous behaviour
                // of just resetting so the next click re-triggers the widget.
              }}
            />
            {userIsClient ? (
              <div className="mb-4 color-red">
                Этот аккаунт другого домена
                <br />
                这个账号是另外一个域名的
              </div>
            ) : null}
            <Controller
              name="phone"
              control={control}
              rules={{ ...required, ...phoneValidation }}
              render={({ field }) => (
                <Input
                  title="Номер телефона"
                  className="mb-4"
                  placeholder="+7"
                  mask="+7 (999) 999-99-99"
                  {...field}
                  error={errors.phone?.message}
                />
              )}
            />
            <Input
              type="password"
              {...register('password', {
                ...required,
              })}
              error={errors.password?.message}
              title="Пароль"
            />
            <Link
              to="/forgot-password"
              className="btn-link d-block link-success mb-4"
            >
              Забыли пароль?
            </Link>
            <button
              type="submit"
              className="btn btn-success w-100"
              disabled={captchaPending}
            >
              Войти
            </button>
          </form>
        </div>
      </div>
      <div />
    </div>
  );
};
